
import { useState, useEffect } from "react";
import { Separator } from "@/components/ui/separator";
import { Investor, Contact } from "@/types/investor";
import { EmailRecord } from "@/types/email";
import { hasInvestorBeenEmailed, loadProviders } from "@/utils/emailUtils";
import { useEmailGenerator } from "@/hooks/useEmailGenerator";
import EmailConfigSection from "./EmailConfigSection";
import InvestorInfoCard from "./InvestorInfoCard";
import EmailContent from "./EmailContent";

interface EmailGeneratorProps {
  investor: Investor;
  contacts: Contact[];
  businessInfo: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  apiKey: string;
  onApiKeyChange: (apiKey: string) => void;
  emails?: EmailRecord[];
  onEmailSent?: () => void;
}

const EmailGenerator = ({ 
  investor, 
  contacts, 
  businessInfo,
  isLoading,
  setIsLoading,
  emails = [],
  onEmailSent
}: EmailGeneratorProps) => {
  // Check directly from database if email has been sent using the latest email data
  const [emailSent, setEmailSent] = useState<boolean>(hasInvestorBeenEmailed(investor.id, emails));
  const [isSending, setIsSending] = useState(false);
  const providers = loadProviders();

  // Update email sent status if investor changes or emails data changes
  useEffect(() => {
    setEmailSent(hasInvestorBeenEmailed(investor.id, emails));
  }, [investor.id, emails]);

  const {
    generatedEmail,
    setGeneratedEmail,
    selectedContact,
    filteredContacts,
    selectedContactData,
    customEmails,
    isCustomContact,
    handleGenerateEmail,
    handleContactChange,
    addCustomEmail,
    setSelectedContactData,
    setIsCustomContact
  } = useEmailGenerator(
    investor,
    contacts,
    businessInfo,
    isLoading,
    setIsLoading
  );

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <EmailConfigSection
            isLoading={isLoading}
            filteredContacts={filteredContacts}
            customEmails={customEmails}
            selectedContact={selectedContact}
            onContactChange={handleContactChange}
            onGenerateEmail={handleGenerateEmail}
            onAddCustomEmail={addCustomEmail}
          />
        </div>

        <div>
          <h3 className="text-lg font-medium mb-3">Investor Information</h3>
          <InvestorInfoCard
            investor={investor}
            selectedContactData={selectedContactData}
            isCustomContact={isCustomContact}
          />
        </div>
      </div>

      <Separator />
      
      <EmailContent
        investor={investor}
        selectedContactData={selectedContactData}
        generatedEmail={generatedEmail}
        setGeneratedEmail={setGeneratedEmail}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isSending={isSending}
        setIsSending={setIsSending}
        emailSent={emailSent}
        setEmailSent={setEmailSent}
        providers={providers}
        onEmailSent={onEmailSent}
        businessInfo={businessInfo}
      />
    </div>
  );
};

export default EmailGenerator;
